/**
 * AppCrashFallback — Top-level crash screen shown by Sentry.ErrorBoundary.
 *
 * This is the last line of defense. It renders when the entire React tree
 * crashes (auth, router, theme providers, etc.). Because of that, it must
 * be completely self-contained — no context, no design tokens, no hooks.
 *
 * Props come from @sentry/react ErrorBoundary:
 *   { error, componentStack, eventId, resetError }
 */

const STYLES = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0a0f1a',
    color: '#e0e6ef',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: 24,
  },
  card: {
    maxWidth: 480,
    width: '100%',
    background: '#111827',
    border: '1px solid #ef444444',
    borderRadius: 12,
    padding: 32,
    boxShadow: '0 4px 12px rgba(0,0,0,.4)',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#ef4444',
    marginBottom: 8,
    letterSpacing: -0.3,
  },
  message: {
    fontSize: 13,
    color: '#94a3b8',
    lineHeight: 1.5,
    marginBottom: 20,
  },
  errorText: {
    fontSize: 11,
    color: '#64748b',
    background: '#0a0f1a',
    borderRadius: 6,
    padding: '8px 12px',
    marginBottom: 20,
    maxHeight: 120,
    overflow: 'auto',
    wordBreak: 'break-word',
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    border: '1px solid #1e293b',
  },
  btnRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  btnPrimary: {
    padding: '8px 20px',
    fontSize: 13,
    fontWeight: 600,
    background: '#22c55e',
    color: '#0a0f1a',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  btnSecondary: {
    padding: '8px 20px',
    fontSize: 13,
    fontWeight: 500,
    background: 'transparent',
    color: '#94a3b8',
    border: '1px solid #1e293b',
    borderRadius: 6,
    cursor: 'pointer',
  },
  eventId: {
    marginTop: 16,
    fontSize: 10,
    color: '#475569',
  },
};

export function AppCrashFallback({ error, resetError, eventId }) {
  return (
    <div style={STYLES.root}>
      <div style={STYLES.card}>
        <div style={STYLES.title}>Something went wrong</div>
        <div style={STYLES.message}>
          The app hit an unexpected error. This has been reported automatically.
          You can try again, or reload the page if the problem persists.
        </div>
        {error?.message && (
          <div style={STYLES.errorText}>{error.message}</div>
        )}
        <div style={STYLES.btnRow}>
          <button
            onClick={() => window.location.reload()}
            style={STYLES.btnPrimary}
          >
            Reload Page
          </button>
          {resetError && (
            <button
              onClick={resetError}
              style={STYLES.btnSecondary}
            >
              Try Again
            </button>
          )}
          <button
            onClick={() => { window.location.href = '/'; }}
            style={STYLES.btnSecondary}
          >
            Go Home
          </button>
        </div>
        {eventId && (
          <div style={STYLES.eventId}>
            Error ID: {eventId}
          </div>
        )}
      </div>
    </div>
  );
}
